import './Chat.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute, useRouter } from 'react-router5';

import { ActionBottomSheet, Header } from '$uikit';
import { History } from '$core/Chat/History/History';
import { SendForm } from '$core/Chat/SendForm/SendForm';
import {
  imActions,
  imAsPeerIdSelector,
  imServiceAccountsSelector,
  imSpendLevelSelector,
  imTypingSelector,
} from '$store/im';
import {
  useChatInfo,
  useCurrentLang,
  useCurrentUserInfo,
  useImActions,
  useTranslator,
  useUserInfo,
  useVisibilityChange,
} from '$hooks';
import {
  IcDollar28,
  IcFolder28,
  IcGallery28,
  IcInfo28,
  IcList28,
  IcMore24,
  IcPhone28,
} from '$assets';
import { scrollToBottom, statReachGoal, transliterate } from '$utils';
import { RootState } from '$store/rootReducer';
import { blacklistActions, blacklistHasBanFromMeSelector } from '$store/blacklist';
import {
  FEED,
  MEDIA,
  PROFILE,
  PROMO_CODE,
  RESOLVE,
  STORAGE,
} from '$shared/constants/pages';
import {
  MetaTags,
  Online,
  SpokenLanguage,
  Typing,
  UnitName,
  Verified,
} from '$shared/components';
import { SpendLevel } from '$core/Chat/SpendLevel/SpendLevel';
import { getPreviousRoute, goBack, goTo, showModal } from '$navigation/helper';
import { ActionBottomSheetOption } from '$uikit/ActionBottomSheet/ActionBottomSheet.interface';
import { WelcomeMessageBanner } from '$core/Chat/WelcomeMessageBanner/WelcomeMessageBanner';
import { FreeChatBanner } from '$core/Chat/FreeChatBanner/FreeChatBanner';
import { balanceActions } from '$store/balance';
import { CurrencyEnum, UserModel } from '$store/models';
import { mainSelector } from '$store/main';

enum ChatActionsGroups {
  INFO = 'info',
  ALERT = 'alert',
}

export const Chat: FC = () => {
  const { route } = useRoute();
  const router = useRouter();
  const peerId = +route.params.peerId;
  const dispatch = useDispatch();
  const im = useImActions();
  const t = useTranslator();
  const peer = useUserInfo(peerId);
  const [isActionShow, setActionsShown] = useState(false);
  const spendLevel = useSelector(imSpendLevelSelector);
  const isTyping = useSelector((root: RootState) => imTypingSelector(root, peerId));
  const isBannedFromMe = useSelector((root: RootState) =>
    blacklistHasBanFromMeSelector(root, peerId),
  );
  const [isFocused, setFocused] = useState(false);
  const curUser = useCurrentUserInfo();
  const chatInfo = useChatInfo(peerId);
  const asPeerId = useSelector(imAsPeerIdSelector);
  const serviceAccounts = useSelector(imServiceAccountsSelector);
  const { users, decryptKeys } = useSelector(mainSelector);
  const showFreeBanner = Object.keys(decryptKeys).length <= 1 && !peer?.isService;
  const serviceAccount = asPeerId ? (users[asPeerId] as UserModel | undefined) : undefined;
  const hasServiceAccounts = serviceAccounts.length > 0;
  const [isServiceSwitchShow, setServiceSwitchShow] = useState(false);

  useEffect(() => {
    im.loadChat(peerId);
  }, []);

  useEffect(() => {
    if (!route.params?.scrollTo && !chatInfo.isLoading) {
      setTimeout(() => {
        scrollToBottom('chat_history', 10);
      }, 0);
    }
  }, [chatInfo.isLoading]);

  useVisibilityChange(
    (focus) => {
      focus && im.readHistory(peerId);
    },
    [im, peerId],
  );

  const currentLang = useCurrentLang();
  const isRU = currentLang.key === 'ru';

  const name = useMemo(() => {
    if (!peer) {
      return t('loading');
    }

    return (
      <div className="Chat__header__wrapper">
        <div className="Chat__header_name_wrap">
          <div className="Chat__header_name">
            <UnitName unit={peer} />
          </div>
          <Verified
            sponsor={!!(peer?.isSponsor || chatInfo?.isSponsor)}
            objectId={peer?.id}
          />
        </div>
        {peerId !== 5 && isTyping ? (
          <Typing className="Chat__header_typing" />
        ) : (
          <div className="Chat__header_online">
            <Online userId={peer.id} extended unit={peer} />{' '}
            <SpendLevel level={spendLevel} />
            <SpokenLanguage user={peer} />
          </div>
        )}
      </div>
    );
  }, [peer, t, isTyping]);

  const handleActions = useCallback(() => {
    setActionsShown(true);
  }, []);

  const handleCall = useCallback(() => {
    dispatch(imActions.setCallPeerId(peerId));
    statReachGoal('call_start', {
      peerId,
    });
  }, [peerId]);

  const handleAction = useCallback(
    (item) => {
      setActionsShown(false);

      if (item === 'ban') {
        dispatch(blacklistActions.ban(peerId));
      } else if (item === 'unban') {
        dispatch(blacklistActions.unban(peerId));
      } else if (item === 'delete') {
        im.deleteDialog(peerId);
      } else if (item === 'sold_messages') {
        router.navigate(STORAGE, {
          peer_id: peerId,
        });
      } else if (item === 'user_info') {
        showModal('user_info');
      } else if (item === 'lists') {
        showModal(`member_lists${peerId}`);
      } else if (item === 'call') {
        handleCall();
      } else if (item === 'gift') {
        showModal(`gift${peerId}`);
      } else if (item == 'tips') {
        dispatch(
          balanceActions.setTipsData({
            peerId,
            currency: CurrencyEnum.EUR,
          }),
        );
      } else if (item === 'media') {
        router.navigate(MEDIA, { peerId }, { replace: true });
      }
    },
    [dispatch, peerId, handleCall],
  );

  const canCall = curUser.verified === 2 && peer?.verified !== 2 && !peer?.isTelegramUser && !asPeerId;

  const actions = useMemo(() => {
    const items: ActionBottomSheetOption[] = [];

    const isUserModel = curUser.verified === 2;
    const isChatWithModel = peer?.verified === 2;

    if (!isUserModel && isChatWithModel && !asPeerId) {
      items.push({
        label: t('chat_gift'),
        icon: <IcDollar28 />,
        value: 'tips',
      });
    }

    if (canCall) {
      items.push({ label: t('chat_call'), value: 'call' });
    }

    if (!asPeerId && (isUserModel || isChatWithModel)) {
      items.push({
        label: t('post_label_subscription_media'),
        value: 'media',
        icon: <IcFolder28 />,
        groupId: ChatActionsGroups.INFO,
      });
    }

    if (curUser.verified === 2 && !asPeerId) {
      items.push(
        {
          label: t('lists_title'),
          value: 'lists',
          icon: <IcList28 />,
        },
        {
          label: t('chat_sold_messages'),
          value: 'sold_messages',
          icon: <IcGallery28 />,
          groupId: ChatActionsGroups.INFO,
        },
      );
    }

    if (curUser.verified === 2) {
      items.push({
        label: t('chat_user_info'),
        value: 'user_info',
        icon: <IcInfo28 />,
        groupId: ChatActionsGroups.INFO,
      });
    }

    if (!asPeerId) {
      items.push(
        isBannedFromMe
          ? { label: t('chat_unblock'), value: 'unban', groupId: ChatActionsGroups.ALERT }
          : { label: t('chat_block'), value: 'ban', groupId: ChatActionsGroups.ALERT },
      );
    }

    items.push({
      label: t('chat_delete'),
      value: 'delete',
      mode: 'alert',
      groupId: ChatActionsGroups.ALERT,
    });

    return items;
  }, [isBannedFromMe, t, curUser, peer, canCall, asPeerId]);

  const serviceAccountOptions = useMemo(() => {
    const items: ActionBottomSheetOption[] = [
      { label: 'My account', value: '0' },
    ];
    serviceAccounts.forEach((id) => {
      const account = users[id] as UserModel | undefined;
      items.push({
        label: account ? `${account.firstName} ${account.lastName}` : `#${id}`,
        value: String(id),
      });
    });
    return items;
  }, [serviceAccounts, users]);

  const handleServiceSwitch = useCallback(
    (value: string) => {
      setServiceSwitchShow(false);
      const newId = +value;
      if (newId === 0) {
        dispatch(imActions.resetAsPeerId());
      } else {
        dispatch(imActions.setAsPeerId(newId));
      }
      dispatch(
        imActions.loadChat({
          peerId,
          asPeerId: newId || undefined,
        }),
      );
    },
    [dispatch, peerId],
  );

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const fullName = peer ? `${peer.firstName} ${peer.lastName}` : '';
  const transliterateName = isRU ? fullName : transliterate(fullName);

  return (
    <>
      <MetaTags
        title={peer ? `${transliterateName} | ${t('chat_title')}` : t('chat_title')}
      />
      <Header
        className="Chat__header"
        showBackButton
        rightIcons={
          peerId === 5
            ? undefined
            : [
                ...(canCall ? [{ icon: <IcPhone28 />, onClick: handleCall }] : []),
                {
                  icon: <IcMore24 />,
                  onClick: handleActions,
                },
              ]
        }
        backButtonOnClick={() => {
          if (getPreviousRoute() === PROMO_CODE) {
            goTo(FEED);
          } else {
            goBack();
          }
        }}
        routeName={peer?.domain ? RESOLVE : PROFILE}
        routeParams={peer?.domain ? { domain: peer.domain } : { id: peerId }}
      >
        {name}
      </Header>
      {hasServiceAccounts && (
        <div
          className="Chat__service_indicator"
          onClick={() => setServiceSwitchShow(true)}
        >
          {serviceAccount
            ? `Writing as ${serviceAccount.firstName} ${serviceAccount.lastName}`
            : 'Writing as yourself'}
          <span className="Chat__service_indicator_switch">
            Switch
          </span>
        </div>
      )}
      {showFreeBanner && <FreeChatBanner peerId={peerId} />}
      <History peerId={peerId} />
      {!asPeerId && !peer?.isService && <WelcomeMessageBanner peerId={peerId} />}
      {peerId !== 5 && (
        <SendForm peerId={peerId} onFocus={handleFocus} onBlur={handleBlur} />
      )}
      <ActionBottomSheet
        options={actions}
        title={transliterateName}
        onChange={handleAction}
        onClose={() => setActionsShown(false)}
        isOpened={isActionShow}
      />
      <ActionBottomSheet
        title="Choose account"
        options={serviceAccountOptions}
        onChange={handleServiceSwitch}
        onClose={() => setServiceSwitchShow(false)}
        isOpened={isServiceSwitchShow}
        isAllGrouped
      />
    </>
  );
};
